import React, { useEffect } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import {
  Switch,
  SwitchLabel,
  typographyVariants,
} from '@mint-lib/tw-components';
import { Suggestions } from '@mint-sdk/api-recommendations';
import { useAtomValue } from 'jotai';

import {
  useBulkPublishActions,
  useIsBulkPublishSelected,
} from '../../../hooks/useBulkPublishSelection.js';
import useSuggestionPermissions from '../../../hooks/useSuggestionPermissions.js';
import { selectedSuggestionAtom } from '../../../store/index.js';

type Props = {
  isPublishing?: boolean;
};

const BulkPublishToggle = ({ isPublishing }: Props) => {
  const { t } = useTranslation('@mint-lib/recommendations-circuit');
  const { isPublishAllowed } = useSuggestionPermissions();
  const { toggle, remove } = useBulkPublishActions();
  const suggestion = useAtomValue(selectedSuggestionAtom);
  const isSelected = useIsBulkPublishSelected(suggestion?.id ?? 0);

  const isEligible =
    suggestion &&
    isPublishAllowed &&
    suggestion.status === Suggestions.Statuses.ACTIVE &&
    !suggestion.enabled;

  useEffect(() => {
    if (!isEligible && isSelected && suggestion) {
      remove(suggestion.id);
    }
  }, [isEligible, isSelected, remove, suggestion]);

  if (!suggestion || !isEligible) {
    return null;
  }

  return (
    <SwitchLabel htmlFor={String(suggestion.id)}>
      <Switch
        id={String(suggestion.id)}
        size="small"
        checked={isSelected}
        disabled={isPublishing}
        onCheckedChange={() => toggle(suggestion)}
        aria-label={t('Ready for publishing')}
      />
      <p className={typographyVariants({ variant: 'bodyShort01' })}>
        {t('Ready for publishing')}
      </p>
    </SwitchLabel>
  );
};

export default BulkPublishToggle;
